import React from 'react';
import { SelectInput, required, useTranslate, useLocaleState } from 'react-admin';
import { RichTextInput, RichTextInputToolbar } from 'ra-input-rich-text';
import { Box } from '@mui/material';
import { DRESS_CODES, COUPLES_RULES } from '../../../constants/events';
import { SourceLanguageBanner } from '../SourceLanguageBanner';
import { useSourceLanguage } from '../../../hooks/useSourceLanguage';

const AGE_RULE_OPTIONS = Array.from({ length: 25 }, (_, i) => i + 6);

/**
 * Event More Details Tab Component
 * Tek kaynak dil alanı — DeepL diğer dillere çevirir.
 */
export const EventMoreDetailsTab = ({ isEdit = false, record = {} }) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  const translatedDressCodes = React.useMemo(
    () =>
      DRESS_CODES.map((item) => ({
        ...item,
        name: translate(`form.dress_code_${item.id}`, { _: item.name }),
      })),
    [translate, locale],
  );

  const translatedCouplesRules = React.useMemo(
    () =>
      COUPLES_RULES.map((item) => ({
        ...item,
        name: translate(`form.couples_rule_${item.id}`, { _: item.name }),
      })),
    [translate, locale],
  );

  const storedAge = record?.rules?.age;
  const ageChoices = React.useMemo(() => {
    const choices = AGE_RULE_OPTIONS.map((age) => ({ id: String(age), name: `${age}+` }));
    const legacy = storedAge != null ? String(storedAge).trim() : '';
    if (legacy && !choices.some((c) => c.id === legacy)) {
      choices.push({ id: legacy, name: legacy });
    }
    return choices;
  }, [storedAge]);

  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />

      <h3>
        {translate('form.event_description', { _: 'Event Description' })}{' '}
        <span style={{ color: 'orange', fontStyle: 'italic', fontSize: 12 }}>
          ({translate('form.required_mark', { _: '*required' })})
        </span>
      </h3>

      <RichTextInput
        source={localizedSource('description')}
        label={translate('form.description', { _: 'Description' })}
        validate={required()}
        fullWidth
        toolbar={<RichTextInputToolbar size="small" />}
      />

      <h3>
        {translate('form.event_rules', { _: 'Event Rules' })}{' '}
        <span style={{ color: 'orange', fontStyle: 'italic', fontSize: 12 }}>
          ({translate('form.required_mark', { _: '*required' })})
        </span>
      </h3>

      <RichTextInput
        source={localizedSource('details')}
        label={translate('form.event_rules', 'Event Rules')}
        validate={required()}
        fullWidth
        toolbar={<RichTextInputToolbar size="small" />}
      />

      <h4>{translate('form.rule_chips', 'Rule Chips')}</h4>
      <span
        style={{
          backgroundColor: '#0ABAB5',
          color: 'white',
          borderRadius: 10,
          fontSize: 12.5,
          padding: 5,
          marginTop: -10,
          marginBottom: 8,
          display: 'inline-block',
        }}
      >
        {translate(
          'form.rule_chips_hint',
          "Rules that are left empty won't appear on the front-end website.",
        )}
      </span>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
          width: '100%',
        }}
      >
        <SelectInput
          source="rules.age"
          label={translate('form.age_rule', 'Age Rule')}
          choices={ageChoices}
          emptyText={translate('form.no_age_rule', { _: 'Yaş sınırı yok' })}
          fullWidth
          defaultValue=""
        />
        <SelectInput
          source="rules.dress"
          label={translate('form.dress_code', 'Kıyafet Kuralı')}
          choices={translatedDressCodes}
          fullWidth
          defaultValue={[]}
        />
        <SelectInput
          source="rules.couples"
          label={translate('form.couples_rule', 'Damsız Girilmez / Kuralı')}
          choices={translatedCouplesRules}
          fullWidth
          defaultValue={[]}
        />
      </Box>
    </React.Fragment>
  );
};
